import { useCallback, useEffect, useState, type JSX } from "react";
import { OpenRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { DealTrackerApi, MERGERS_TRACKER_TYPES } from "@/services/api/deal-tracker-api";
import { DynamicFieldsApi } from "@/services/api/dynamic-fields-api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/services/auth/use-auth";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { formatNumericCalendarDate } from "@/utils/calendar-date";
import type { TriyoDynamicFieldTable } from "@/types/models";
import { DealTrackerConfirmation } from "./DealTrackerConfirmation";
import { DealTrackerTable } from "./DealTrackerTable";
import {
  addRow,
  changedRowCount,
  deleteRow,
  hasChanges,
  resolveDealTracker,
  setCellValue,
  toSaveRequest,
} from "./deal-tracker-rows";
import type { DealTrackerSource, MergersAndAcquisitionsRow } from "./deal-tracker-types";
import "./DealTracker.scss";

/**
 * Finds the signed-in Banker's own tracker. Which of the two M&A widgets holds
 * it depends on whether they are an Analyst or an Associate, and nothing in the
 * session says which — so both are asked and the row belonging to this user wins.
 */
async function loadDealTracker(userId?: number): Promise<DealTrackerSource | null> {
  for (const type of MERGERS_TRACKER_TYPES) {
    const rows = await DealTrackerApi.getMergersAndAcquisitions(type);
    const own = userId != null ? rows.find((row) => row.UserId === userId) : rows.length === 1 ? rows[0] : undefined;
    if (!own?.DealsTaskId) continue;

    const fields = await DynamicFieldsApi.getTaskDetailDynamicFields(
      own.DealsTaskId,
      own.DealsTaskTypeId ?? 0,
      own.DealsProjectTypeId ?? 0
    );
    const source = resolveDealTracker(type, own, fields);
    if (source) return source;
  }
  return null;
}

/**
 * Loads the tracker for one named Banker — the Staffer drill-down (TAD-6073).
 * The row already carries the addressing, so this is the dynamic-fields call only.
 */
async function loadBankerDealTracker(banker: MergersAndAcquisitionsRow): Promise<DealTrackerSource | null> {
  if (!banker.DealsTaskId) return null;

  const fields = await DynamicFieldsApi.getTaskDetailDynamicFields(
    banker.DealsTaskId,
    banker.DealsTaskTypeId ?? 0,
    banker.DealsProjectTypeId ?? 0
  );
  return resolveDealTracker(banker.Name ?? "", banker, fields);
}

type Outcome = { detail: string } | null;

export interface DealTrackerProps {
  onClose?: () => void;
  /**
   * Edit this Banker's tracker instead of resolving the signed-in user's own —
   * the Staffer drill-down. Entitlement is still enforced by the API on save.
   */
  banker?: MergersAndAcquisitionsRow;
  /**
   * TAD-6073: the Staffer view is the Banker view minus "Indicate No Update".
   * Defaults to showing it, which is the Banker's own view.
   */
  showIndicateNoUpdate?: boolean;
  /**
   * Fired once the tracker has actually been written to — a saved grid or a
   * recorded "no update". Hosts that cache a summary of this tracker use it to
   * know their copy is now stale.
   */
  onSaved?: () => void;
}

export function DealTracker({ onClose, banker, showIndicateNoUpdate = true, onSaved }: DealTrackerProps): JSX.Element {
  const { user } = useAuth();
  const fetcher = useCallback(
    () => (banker ? loadBankerDealTracker(banker) : loadDealTracker(user?.Id)),
    [banker, user?.Id]
  );
  const { data: source, loading, error, refetch } = useApi(fetcher);

  const [table, setTable] = useState<TriyoDynamicFieldTable | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [outcome, setOutcome] = useState<Outcome>(null);

  useEffect(() => {
    setTable(source ? source.table : null);
    setOutcome(null);
    setSaveError("");
  }, [source]);

  const dashboardUrl = resolveLaunchUrl("dashboard");

  if (loading) {
    return (
      <div className="deal-tracker__state" role="status">
        <FluentSpinner label="Opening your Deal Tracker…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="deal-tracker__state" role="alert">
        <p>{error}</p>
        <button type="button" className="deal-tracker__secondary-button" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (!source || !table) {
    return (
      <div className="deal-tracker__empty">
        <h3>No Deal Tracker</h3>
        <p>Your TRIYO access does not include an M&amp;A Deal Tracker you can update from Outlook.</p>
        {dashboardUrl ? (
          <a href={dashboardUrl} target="_blank" rel="noreferrer" className="deal-tracker__web-link">
            Open TRIYO dashboard <OpenRegular />
          </a>
        ) : null}
      </div>
    );
  }

  if (outcome) {
    return (
      <DealTrackerConfirmation
        detail={outcome.detail}
        // Without a host to close, Done goes back to the tracker -- reloaded,
        // since the rows and Last Update on screen are now the pre-save ones.
        onDone={() => (onClose ? onClose() : void refetch())}
      />
    );
  }

  const dirty = hasChanges(source.table, table);

  const runSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError("");
    try {
      // The dashboard route rather than the generic dynamic-fields one: it is the
      // only save path that checks the caller may edit this Banker's Task.
      const [payload] = toSaveRequest(source, table);
      await DealTrackerApi.saveDealTracker(payload);
      const count = changedRowCount(source.table, table);
      onSaved?.();
      setOutcome({ detail: `Deal Tracker · ${count} deal${count === 1 ? "" : "s"} updated` });
    } catch {
      // The unsaved table stays on screen so nothing typed is lost (TAD-6037).
      setSaveError("Your Deal Tracker could not be saved. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const runNoUpdate = async (): Promise<void> => {
    setSaving(true);
    setSaveError("");
    try {
      await DealTrackerApi.markNoUpdateRequired(source.taskId);
      // Pending edits are deliberately discarded -- "no update" records exactly
      // that, and the reset keeps the screen honest about what was saved.
      setTable(source.table);
      onSaved?.();
      setOutcome({ detail: "Deal Tracker · marked up to date — no changes" });
    } catch {
      setSaveError("TRIYO could not record your update. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="deal-tracker">
      <div className="deal-tracker__context">
        <div>
          <span>{source.taskName}</span>
          <strong>
            {source.lastUpdate ? `Last updated ${formatNumericCalendarDate(source.lastUpdate)}` : "Never updated"}
          </strong>
        </div>
        <span className="deal-tracker__row-count">
          {table.Rows.length} row{table.Rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {source.canEdit ? null : (
        <p className="deal-tracker__message" role="status">
          This Deal Tracker is read-only for your account.
        </p>
      )}

      <DealTrackerTable
        table={table}
        disabled={!source.canEdit || saving}
        onCellChange={(rowKey, columnId, value) => setTable(setCellValue(table, rowKey, columnId, value))}
        onAddRow={() => setTable(addRow(table))}
        onDeleteRow={(rowKey) => setTable(deleteRow(table, rowKey))}
      />

      {saveError ? (
        <p className="deal-tracker__error" role="alert">
          {saveError}
        </p>
      ) : null}

      {source.canEdit ? (
        <div className="deal-tracker__actions">
          <button
            type="button"
            className="deal-tracker__tertiary-button"
            disabled={saving}
            onClick={() => (dirty ? setTable(source.table) : onClose?.())}
          >
            Cancel
          </button>
          {/* TAD-6073: a Staffer records changes or nothing -- marking someone
              else's tracker "up to date" is not theirs to assert. */}
          {showIndicateNoUpdate ? (
            <button
              type="button"
              className="deal-tracker__secondary-button"
              disabled={saving}
              onClick={() => void runNoUpdate()}
            >
              Indicate No Update
            </button>
          ) : null}
          <button
            type="button"
            className="deal-tracker__primary-button"
            disabled={saving || !dirty}
            onClick={() => void runSave()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
